import React, {useEffect, useState} from 'react';
import {
  IconButton,
  FormControl,
  TextInput,
  Select,
  Button,
  Spinner
} from '@contentful/f36-components';
import {ListBulletedIcon, EditIcon} from '@contentful/f36-icons'
import {useSDK} from '@contentful/react-apps-toolkit';
import {
  fetchServiceTypes,
  sendTranslationOrderHuman,
  sendTranslationOrderMachine,
} from '../services/translated';
import PoweredBy from './PoweredBy';

const SidebarTranslationEnabled = (props) => {

  const {onTranslationOrderSent, busyLanguages} = props

  const sdk = useSDK();

  const [locales, setLocales] = useState([]);
  const [sourceLocale, setSourceLocale] = useState(null);
  const [targetLocales, setTargetLocales] = useState([]);
  const [untranslatableWarning, setUntranslatableWarning] = useState('No translatable text fields or references found in master language.')
  const [serviceTypes, setServiceTypes] = useState([])
  const [serviceType, setServiceType] = useState(null)
  const [translationType, setTranslationType] = useState('HT')
  const [instructions, setInstructions] = useState('')
  const [style, setStyle] = useState('faithful')
  const [context, setContext] = useState('')
  const [isTranslatable, setIsTranslatable] = useState(false)
  const [loading, setLoading] = useState(false)

  const {space, environment, id: entryId} = sdk.entry.getSys();

  useEffect(() => {
    if (!sourceLocale) return
    setUntranslatableWarning(`No translatable text fields or references found in source language (${sourceLocale.code}).`)

    const localizableFields = sdk.contentType.fields.filter(field => field.localized || field.type === 'reference').map(({id}) => id);
    const translatable = Object.entries(sdk.entry.fields)
      .some(([name, field]) => {
        try {
          return !!field.getValue(sourceLocale.code) && localizableFields.includes(name)
        } catch {
          return false
        }
      })
    setIsTranslatable(translatable)
  }, [sdk.contentType.fields, sdk.entry.fields, sourceLocale])

  useEffect(() => {

    const master = Object.entries(sdk.locales.names)
      .map(([code, name]) => ({code, name}))
      .filter(({code}) => code === sdk.locales.default)[0]

    setSourceLocale(master)

    // Set available locales and target locales
    const locales = Object.entries(sdk.locales.names)
      .map(([code, name]) => ({code, name}));
    setLocales(locales);
    setTargetLocales(locales.slice().filter(({code}) => code !== sdk.locales.default && !busyLanguages.includes(code)));

    // Get service types from the connector
    (async () => {
      let availableServiceTypes
      try {
        availableServiceTypes = await fetchServiceTypes({apiKey: sdk.parameters.installation.apiKey, spaceId: space.sys.id})
        // Set available service types
        setServiceTypes(availableServiceTypes.serviceTypes);

        if (availableServiceTypes.defaultServiceType && availableServiceTypes.serviceTypes.find(st => st.name === availableServiceTypes.defaultServiceType)) {
          // Set active service type, if any
          setServiceType(availableServiceTypes.defaultServiceType)
        }
      } catch (e) {
        sdk.notifier.error(e.response.data)
      }
    })();

    // Retrieve localizable fields of the entry
    const localizableFields = sdk.contentType.fields.filter(field => field.localized || field.type === 'reference').map(({id}) => id);

    const checkIfEntryIsTranslatable = () => Object
      .entries(sdk.entry.fields)
      .some(([name, field]) => !!field.getValue() && localizableFields.includes(name))

    setIsTranslatable(checkIfEntryIsTranslatable())

    sdk.entry.onSysChanged((value) => {
      setIsTranslatable(checkIfEntryIsTranslatable())
    })

  }, [busyLanguages, sdk, space.sys.id])

  // stPropToCheck values: 'human_enabled' | 'machine_enabled'
  const serviceTypeOptions = (stPropToCheck) => serviceTypes.filter(servType => servType[stPropToCheck]).map(servType => (
    <Select.Option key={servType.name} value={servType.name}>{servType.name}</Select.Option>
  ))

  const onSelectLanguagesButtonClick = () => {
    sdk.dialogs.openCurrentApp({
      width: 520,
      title: "Target languages",
      shouldCloseOnOverlayClick: true,
      parameters: {
        type: 'locales',
        locales,
        targetLocales,
        busyLanguages,
        sourceLocale: sourceLocale ? sourceLocale.code : sdk.locales.default,
      }
    }).then(data => {
      if (!data) return
      setTargetLocales(data.targetLocales)
    })
  }

  const onInstructionButtonClick = () => {
    sdk.dialogs.openCurrentApp({
      width: 520,
      title: "Instructions",
      shouldCloseOnOverlayClick: true,
      parameters: {
        type: 'instructions',
        instructions
      }
    }).then(data => {
      if (!data) return
      setInstructions(data.instructions)
    })
  }

  const onContextButtonClick = () => {
    sdk.dialogs.openCurrentApp({
      width: 520,
      title: "Context",
      shouldCloseOnOverlayClick: true,
      parameters: {
        type: 'context',
        context
      }
    }).then(data => {
      if (!data) return
      setContext(data.context)
    })
  }

  const languageTextInputValue = () => {
    if (targetLocales.length === locales.length) return `All locales (${targetLocales.length})`
    if (targetLocales.length === 0) return 'No locales selected'
    return targetLocales.map(({name}) => name).join()
  }

  const instructionsTextInputValue = () => {

    if (!instructions || instructions.length === 0) return 'None'

    const formattedInstructions = instructions.replaceAll('\n', ' ')

    if (formattedInstructions.length < 32) return formattedInstructions
    else return `${formattedInstructions.substring(0, 32)}...`
  }

  const buttonContent = () => {
    if (!loading) return <span>Submit order</span>
    else return <Spinner variant="white"/>
  }

  const sendOrder = async (translationType) => {
    setLoading(true)

    const {firstName, lastName, email} = sdk.user

    let response;
    try {
      if (translationType === 'MT') {
        response = await sendTranslationOrderMachine({
          apiKey: sdk.parameters.installation.apiKey,
          entryId,
          environmentId: environment.sys.id,
          spaceId: space.sys.id,
          source: sourceLocale.code,
          targets: targetLocales.map(l => l.code),
          serviceType,
          style,
          context,
          sender: {firstName, lastName, email},
        })
      } else {
        response = await sendTranslationOrderHuman({
          apiKey: sdk.parameters.installation.apiKey,
          entryId,
          environmentId: environment.sys.id,
          spaceId: space.sys.id,
          source: sourceLocale.code,
          targets: targetLocales.map(l => l.code),
          serviceType,
          instructions,
          sender: {firstName, lastName, email},
        })
      }
    } catch (e) {
      if (e.response.data === "Connector error message: Connector error message: Entry has not translatable content") {
        sdk.notifier.error(untranslatableWarning);
      } else {
        sdk.notifier.error(e.response.data)
      }
      return
    } finally {
      setLoading(false)
    }
    switch (response.response) {
      case 'Request sent':
        sdk.notifier.success(`Translations order sent!`);
        break;
      case 'Request sent, contents will be delivered shortly':
        sdk.notifier.success(`Translations order sent, contents will be delivered shortly!`);
        break;
      case 'Request sent with errors':
      case 'Request sent with errors, contents will be delivered shortly':
        sdk.notifier.warning(`Translations order sent with errors, please contact support`);
        break;
      case 'Request failed':
        sdk.notifier.error(`Translations order failed, please contact support`);
        break;
      case `Parameter 'context' contains more than 10 elements`:
        sdk.notifier.error(`Parameter 'context' contains more than 10 elements`);
        break;
    }
    // sdk.notifier.success(`Translations order sent!`)
    onTranslationOrderSent(translationType)
  }

  const onSendOrdersButtonClick = async (translationType) => {

    if (!isTranslatable) {
      sdk.notifier.error(untranslatableWarning);
      return
    }

    if (sdk.parameters.installation.askConfirmation) {

      let entryTitle = sdk.contentType.displayField && sdk.entry.fields[sdk.contentType.displayField] ? sdk.entry.fields[sdk.contentType.displayField].getValue() : 'Untitled'

      const message = `
      You are ordering translation${targetLocales.length > 1 ? 's' : ''} for entry "${entryTitle}" in 
      ${targetLocales.length > 1 ? `${targetLocales.length} locales` : targetLocales[0].name}.
      Do you want to continue?      
      `

      sdk.dialogs
        .openConfirm({
          title: 'Order translations?',
          message,
          intent: 'positive',
          confirmLabel: 'Confirm',
          cancelLabel: 'Dismiss'
        })
        .then(async (result) => {
          if (result) await sendOrder(translationType)
        })
    } else {
      await sendOrder(translationType)
    }
  }

  const handleServiceTypeChange = (event) => {
    setServiceType(event.target.value);
  };

  const handleTranslationTypeChange = (event) => {
    setTranslationType(event.target.value);
    setServiceType(null);
  };

  const handleSourceLocaleChange = (event) => {
    const newSourceCode = event.target.value;
    const newSource = locales.find(l => l.code === newSourceCode);
    setSourceLocale(newSource);
    setTargetLocales(locales.filter(({code}) => code !== newSourceCode && !busyLanguages.includes(code)));
  };

  const handleStyleChange = (event) => {
    setStyle(event.target.value);
  };

  return (
      <div>
        <FormControl isRequired margin="none">
          <FormControl.Label>Translation type</FormControl.Label>
          <Select
              name="translationType"
              id="translationType"
              value={translationType || ''}
              onChange= {e => handleTranslationTypeChange(e)}
          >
            <Select.Option key='Human Translation' value='HT'>Human Translation</Select.Option>
            <Select.Option key='Machine Translation' value='MT'>Machine Translation</Select.Option>
          </Select>
        </FormControl>
        <FormControl isRequired margin="none">
          <FormControl.Label>Source language</FormControl.Label>
          <Select
              name="sourceLocale"
              id="sourceLocale"
              value={sourceLocale ? sourceLocale.code : ''}
              onChange={e => handleSourceLocaleChange(e)}
          >
            {locales.map(locale => (
              <Select.Option key={locale.code} value={locale.code}>{locale.name} ({locale.code})</Select.Option>
            ))}
          </Select>
        </FormControl>
        <FormControl isRequired margin="none">
          <FormControl.Label>Target languages</FormControl.Label>
          <TextInput.Group>
            <TextInput
                value={languageTextInputValue()}
                isDisabled={true}
            />
            <IconButton
                icon={<ListBulletedIcon/>}
                aria-label="List"
                variant="secondary"
                onClick={onSelectLanguagesButtonClick}
            />
          </TextInput.Group>
        </FormControl>
        {translationType === 'HT' ? (
            <>
              <FormControl isRequired margin="none">
                <FormControl.Label>Service Type</FormControl.Label>
                <Select
                    name="serviceType"
                    id="serviceType"
                    value={serviceType || ''}
                    onChange={e => handleServiceTypeChange(e)}
                >
                  <Select.Option value="" isDisabled>
                    Please select an option
                  </Select.Option>
                  {serviceTypeOptions('human_enabled')}
                </Select>
              </FormControl>
              <FormControl margin="none">
                <FormControl.Label>Instructions</FormControl.Label>
                <TextInput.Group>
                  <TextInput
                      value={instructionsTextInputValue()}
                      isDisabled={true}
                  />
                  <IconButton
                      icon={<EditIcon/>}
                      aria-label="List"
                      variant="secondary"
                      onClick={onInstructionButtonClick}
                  />
                </TextInput.Group>
              </FormControl>
              <div style={{display: 'flex'}}>
                <div
                    style={{
                      display: 'grid',
                      fontWeight: 'bold',
                      alignItems: 'center',
                      marginLeft: '32px',
                      marginRight: '32px',
                      cursor: 'pointer'
                    }}
                    onClick={props.onCancel}
                >Cancel
                </div>
                <div style={{flexGrow: 1}}>
                  <Button
                      variant="primary"
                      isFullWidth
                      isDisabled={targetLocales.length === 0 || !serviceType || serviceType === '' || loading}
                      onClick={() => onSendOrdersButtonClick('HT')}
                  >{buttonContent()}</Button>
                </div>
              </div>
            </>
        ) : (
            <>
              <FormControl isRequired margin="none">
                <FormControl.Label>Service Type</FormControl.Label>
                <Select
                    name="serviceType"
                    id="serviceType"
                    value={serviceType || ''}
                    onChange={e => handleServiceTypeChange(e)}
                >
                  <Select.Option value="" isDisabled>
                    Please select an option
                  </Select.Option>
                  {serviceTypeOptions('machine_enabled')}
                </Select>
              </FormControl>
              <FormControl isRequired margin="none">
                <FormControl.Label>Style</FormControl.Label>
                <Select
                    name="style"
                    id="style"
                    value={style || ''}
                    onChange={e => handleStyleChange(e)}
                >
                  <Select.Option value="" isDisabled>
                    Please select an option
                  </Select.Option>
                  <Select.Option key='Fluid' value='fluid'>Fluid</Select.Option>
                  <Select.Option key='Creative' value='creative'>Creative</Select.Option>
                  <Select.Option key='Faithful' value='faithful'>Faithful</Select.Option>
                </Select>
              </FormControl>
              <FormControl margin="none">
                <FormControl.Label>Context</FormControl.Label>
                <TextInput.Group>
                  <TextInput
                      value={context || ''}
                      isDisabled={true}
                  />
                  <IconButton
                      icon={<EditIcon/>}
                      aria-label="List"
                      variant="secondary"
                      onClick={onContextButtonClick}
                  />
                </TextInput.Group>
              </FormControl>
              <div style={{display: 'flex'}}>
                <div
                    style={{
                      display: 'grid',
                      fontWeight: 'bold',
                      alignItems: 'center',
                      marginLeft: '32px',
                      marginRight: '32px',
                      cursor: 'pointer'
                    }}
                    onClick={props.onCancel}
                >Cancel
                </div>
                <div style={{flexGrow: 1}}>
                  <Button
                      variant="primary"
                      isFullWidth
                      isDisabled={targetLocales.length === 0 || !serviceType || serviceType === '' || loading || !style || style === ''}
                      onClick={() => onSendOrdersButtonClick('MT')}
                  >{buttonContent()}</Button>
                </div>
              </div>
            </>
        )}
        <PoweredBy/>
      </div>

  )
}

export default SidebarTranslationEnabled;